/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOOLTIP_UPDATES_INVENTORY_HEADER
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Added tooltips to inventory action buttons and sort dropdown.
 *
 * File Size Check:
 * - Current: 191 lines
 * - Adding: ~20 lines
 * - Projected: 211 lines
 * - Decision: Proceed
 *
 * Related Files:
 * - client/src/constants/tooltips.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    INVENTORY_HEADER_RESPONSIVE_IMPROVEMENTS
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Added responsive wrapper classes to support flexible button wrapping and mobile stacking.
 * Buttons now wrap on tablets and stack vertically on mobile devices for better usability.
 *
 * File Size Check:
 * - Current: ~165 lines
 * - Adding: ~3 lines (wrapper classes)
 * - Projected: ~168 lines
 * - Decision: Proceed (well within 200-line limit)
 *
 * Typography Compliance (UI files only):
 * - Utilities used: N/A (layout structure only)
 * - Tokens used: N/A
 *
 * Related Files:
 * - client/src/features/inventory/inventory-page/components/InventoryHeader.scss
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    INVENTORY_HEADER_MY_INVENTORY_BUTTON
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Added "My Inventory" button to inventory header, positioned after the "Download template"
 * dropdown. Button uses btn-my-inventory class with faArrowDown icon, matching Figma design.
 *
 * File Size Check:
 * - Current: ~132 lines
 * - Adding: ~7 lines
 * - Projected: ~139 lines
 * - Decision: Proceed
 *
 * Typography Compliance (UI files only):
 * - Utilities used: N/A (button uses btn-my-inventory class)
 * - Tokens used: N/A
 *
 * Related Files:
 * - client/src/styles/components/_buttons.scss
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    INVENTORY_PAGE_SPLIT_COMPONENTS_HEADER
 * Date:         2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Extracted InventoryHeader component from InventoryPage.tsx.
 */

import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUp, faArrowDown, faCheck } from "@fortawesome/free-solid-svg-icons";
import { User } from "../../../../interfaces/User";
import { ACTION_BUTTON_TOOLTIPS } from "../../../../constants/tooltips";
import "./InventoryHeader.scss";
import "./InventoryHeader.responsive.scss";

interface InventoryHeaderProps {
  /** [2026-09-16] total items in the catalog, shown next to the title */
  totalItems?: number;
  profile: User | null;
  onAddItem: () => void;
  onUploadClick: () => void;
  isUploading: boolean;
  onDownloadTemplate: (ext: "csv" | "xlsx") => void;
  sort: string;
  onSortChange: (value: string) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleFilePicked: (e: React.ChangeEvent<HTMLInputElement>) => void;
  // [2026-02-07] BR-16/BR-17: Confirm and export props
  onConfirmInventory: () => void;
  onExportCSV: () => void;
  isExporting?: boolean;
  lastConfirmedAt?: string | null;
}

const InventoryHeader: React.FC<InventoryHeaderProps> = ({
  profile,
  onAddItem,
  onUploadClick,
  isUploading,
  onDownloadTemplate,
  sort,
  onSortChange,
  fileInputRef,
  handleFilePicked,
  onConfirmInventory,
  onExportCSV,
  isExporting = false,
  totalItems,
  lastConfirmedAt,
}) => {
  return (
    <div className="bg-light border-top border-bottom py-3 px-3 inventory-header-container">
      {/* [2026-09-16] show the catalog size here. It previously appeared only in the
          pagination row at the very bottom, so after a 97,296-item upload there was nothing
          near the top telling the seller how much stock is actually live. */}
      <h2 className="m-0 u-H2-24 text-color-black">
        My Inventory
        {typeof totalItems === "number" && totalItems > 0 && (
          <span className="u-Body-16 text-color-gray ms-2">
            ({totalItems.toLocaleString()} items)
          </span>
        )}
      </h2>
      <div className="d-flex align-items-center gap-2 inventory-header-actions-row">
        <div className="d-flex align-items-center gap-2 inventory-header-button-group">
          <button
            type="button"
            className="btn-add-item"
            onClick={onAddItem}
            aria-label="Add inventory item"
            title={ACTION_BUTTON_TOOLTIPS.ADD_ITEM}
          >
            <span className="me-1">+</span>
            <span className="inv-btn-label">Add Item</span>
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            className="d-none"
            onChange={handleFilePicked}
          />

          <button
            className="btn-upload-file"
            onClick={onUploadClick}
            disabled={isUploading}
            aria-label="Upload inventory file"
            title={ACTION_BUTTON_TOOLTIPS.UPLOAD_FILE}
          >
            {isUploading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span className="inv-btn-label">Uploading...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faArrowUp} />
                <span className="inv-btn-label">Upload file</span>
              </>
            )}
          </button>

          {/* [2026-02-08] Download Inventory - moved after Upload file */}
          <button
            className="btn-my-inventory"
            onClick={onExportCSV}
            disabled={isExporting}
            aria-label="Download My Inventory"
            title={ACTION_BUTTON_TOOLTIPS.MY_INVENTORY}
          >
            <FontAwesomeIcon icon={faArrowDown} />
            <span className="inv-btn-label">{isExporting ? "Downloading..." : "Download Inventory"}</span>
          </button>

          <div className="dropdown">
            <button
              className="btn-template dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-label="Download inventory template"
              title={ACTION_BUTTON_TOOLTIPS.DOWNLOAD_TEMPLATE}
            >
              <FontAwesomeIcon icon={faArrowDown} />
              <span className="inv-btn-label">Download template</span>
            </button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li>
                <button
                  className="dropdown-item p-small"
                  onClick={() => onDownloadTemplate("csv")}
                >
                  CSV
                </button>
              </li>
              <li>
                <button
                  className="dropdown-item p-small"
                  onClick={() => onDownloadTemplate("xlsx")}
                >
                  XLSX
                </button>
              </li>
            </ul>
          </div>

          {/* [2026-02-07] BR-16: Confirm Inventory button */}
          <div className="d-inline-flex align-items-center">
            <button
              className="btn-my-inventory"
              onClick={onConfirmInventory}
              aria-label="Confirm Inventory"
            >
              <FontAwesomeIcon icon={faCheck} />
              <span className="inv-btn-label">Confirm</span>
            </button>
            {lastConfirmedAt && (
              <span className="u-Label-12-medium text-color-grey-2 ms-2">
                {(() => {
                  const days = Math.floor(
                    (Date.now() - new Date(lastConfirmedAt).getTime()) / (24 * 60 * 60 * 1000)
                  );
                  if (days > 7) {
                    return <span className="text-color-error">Overdue ({days}d ago)</span>;
                  }
                  return `${days}d ago`;
                })()}
              </span>
            )}
          </div>

        </div>
        <div className="inventory-header-sort-group d-flex align-items-center">
          <span className="u-P3-14-medium text-color-black text-nowrap">Sort by:</span>
          <select
            className="form-select form-select-sm inventory-sort-dropdown"
            value={sort}
            onChange={(e) => onSortChange(e.target.value)}
            aria-label="Sort inventory items"
            title={ACTION_BUTTON_TOOLTIPS.SORT_INVENTORY}
          >
            <option value="newest">Newest</option>
            <option value="price_desc">Price: High → Low</option>
            <option value="price_asc">Price: Low → High</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default InventoryHeader;
